import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { useStrikeTeams } from 'components/IAP/hooks/strikeTeamHooks';
import { useAors } from 'hooks/useAors';
import { useRosters } from 'hooks/useRosters';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import {
  AUTOMATION_CHANNEL_OPTIONS,
  AUTOMATION_PRIORITY_OPTIONS,
  withoutLockedChannels,
} from 'components/MapPage/CorporateViewTabs/EventsTab/automationNotifyForm';
import {
  CheckboxRow,
  SimpleSelect,
  ToggleRow,
} from 'components/MapPage/CorporateViewTabs/EventsTab/automationFormControls';
import { ChecklistConfigEditor } from 'components/MapPage/CorporateViewTabs/EventsTab/ChecklistConfigEditor';
import EscalationPlanField from 'components/MapPage/CorporateViewTabs/NotificationsTab/EscalationPlanField';
import {
  ESCALATION_PRESETS,
  escalationPresetKeyOf,
  type EscalationPresetKey,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/escalationPlanModel';
import {
  useArchiveScenario,
  useCreateScenario,
  useUpdateScenario,
  type Scenario,
} from 'hooks/scenarios/scenarioHooks';
import {
  blankScenarioForm,
  formToScenarioPayload,
  scenarioFormErrors,
  scenarioToForm,
  type ScenarioFormValues,
} from './scenarioFormModel';

type Props = {
  /** Null while creating a new scenario. */
  scenario: Scenario | null;
  onClose: () => void;
};

/**
 * Edits a saved response: who it reaches, what it says, how it chases silence,
 * and what work it creates.
 *
 * The checklist and channel controls are the automation rule editor's own, so a
 * response configured as a scenario and one configured as a rule's consequence
 * are described the same way and behave the same way when fired.
 */
export const ScenarioEditor: React.FC<Props> = ({ scenario, onClose }) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: rosters } = useRosters(groupGuid ? [groupGuid] : []);
  const { data: aors = [] } = useAors();
  const { data: strikeTeams = [] } = useStrikeTeams();

  const createScenario = useCreateScenario();
  const updateScenario = useUpdateScenario();
  const archiveScenario = useArchiveScenario();

  const form = useForm<ScenarioFormValues>({
    defaultValues: scenario ? scenarioToForm(scenario) : blankScenarioForm(),
  });

  useEffect(() => {
    const next = scenario ? scenarioToForm(scenario) : blankScenarioForm();
    const key = escalationPresetKeyOf(next.escalationPlan);
    setEscalationPreset(key);
    form.reset({ ...next, escalationPlan: ESCALATION_PRESETS[key] });
  }, [scenario, form]);

  const values = useWatch({ control: form.control }) as ScenarioFormValues;
  const checklistItems = form.watch('checklistItems') ?? [];
  const escalationPlan = form.watch('escalationPlan') ?? null;
  const confirmReceipt = !!form.watch('confirmReceipt');

  const [escalationPreset, setEscalationPreset] = useState<EscalationPresetKey>(
    () => escalationPresetKeyOf(scenario?.escalationPlan ?? null)
  );

  const userOptions = useMemo(
    () =>
      (rosters ?? []).map((user) => ({
        value: user.user_guid,
        label: user.email_address,
      })),
    [rosters]
  );

  const teamOptions = useMemo(
    () =>
      strikeTeams.map((team) => ({
        value: team.strike_team_id || team.id,
        label: team.name,
      })),
    [strikeTeams]
  );

  const aorOptions = useMemo(
    () =>
      aors
        .filter((aor) => Boolean(aor.item_id))
        .map((aor) => ({ value: String(aor.item_id), label: aor.name })),
    [aors]
  );

  const errors = scenarioFormErrors({
    ...values,
    checklistItems,
    escalationPlan,
  });

  const onSubmit = form.handleSubmit(async (submitted) => {
    const payload = formToScenarioPayload(submitted, scenario);

    if (scenario) {
      await updateScenario.mutateAsync({ id: scenario.id, ...payload });
    } else {
      await createScenario.mutateAsync(payload);
    }
    onClose();
  });

  const isSaving = createScenario.isPending || updateScenario.isPending;

  return (
    <form className="tw:flex tw:flex-col tw:gap-5" onSubmit={onSubmit}>
      <div className="tw:space-y-3">
        <Field>
          <FieldLabel>Name</FieldLabel>
          <Controller
            control={form.control}
            name="name"
            render={({ field }) => (
              <Input
                placeholder="e.g. Evacuation order — coastal zone"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </Field>
        <Field>
          <FieldLabel>Description</FieldLabel>
          <Controller
            control={form.control}
            name="description"
            render={({ field }) => (
              <Textarea
                rows={2}
                placeholder="What this scenario is for, and when to launch it."
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <FieldDescription>
            Used as the message body when no body template is set.
          </FieldDescription>
        </Field>
      </div>

      <section className="tw:space-y-3">
        <h3 className="tw:m-0 tw:text-sm tw:font-semibold">Who it reaches</h3>
        <Field>
          <FieldLabel>People</FieldLabel>
          <Controller
            control={form.control}
            name="userIds"
            render={({ field }) => (
              <MultiIdCombobox
                options={userOptions}
                value={field.value ?? []}
                onChange={field.onChange}
                placeholder="Select people…"
              />
            )}
          />
        </Field>
        <Field>
          <FieldLabel>Teams</FieldLabel>
          <Controller
            control={form.control}
            name="teamIds"
            render={({ field }) => (
              <MultiIdCombobox
                options={teamOptions}
                value={field.value ?? []}
                onChange={field.onChange}
                placeholder="Select teams…"
              />
            )}
          />
        </Field>
        <Field>
          <FieldLabel>Areas</FieldLabel>
          <Controller
            control={form.control}
            name="aorItemIds"
            render={({ field }) => (
              <MultiIdCombobox
                options={aorOptions}
                value={field.value ?? []}
                onChange={field.onChange}
                placeholder="Select areas of responsibility…"
              />
            )}
          />
          <FieldDescription>
            Resolved through area coverage when the scenario launches, so the
            audience reflects who covers the area that day rather than who
            covered it when this was saved.
          </FieldDescription>
        </Field>
      </section>

      <section className="tw:space-y-3">
        <h3 className="tw:m-0 tw:text-sm tw:font-semibold">What it says</h3>
        <Field>
          <FieldLabel>Channels</FieldLabel>
          <Controller
            control={form.control}
            name="notificationTypes"
            render={({ field }) => (
              <div className="tw:space-y-2">
                <MultiIdCombobox
                  options={AUTOMATION_CHANNEL_OPTIONS.filter(
                    (option) => !('disabled' in option && option.disabled)
                  ).map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  value={withoutLockedChannels(field.value)}
                  onChange={(next) =>
                    field.onChange(withoutLockedChannels(next))
                  }
                  placeholder="Select channels…"
                />
                {AUTOMATION_CHANNEL_OPTIONS.filter(
                  (option) => 'disabled' in option && option.disabled
                ).map((option) => (
                  <CheckboxRow
                    key={option.value}
                    label={option.label}
                    checked={false}
                    locked
                    tooltip={'tooltip' in option ? option.tooltip : undefined}
                    onChange={() => {}}
                  />
                ))}
              </div>
            )}
          />
        </Field>
        <Field>
          <FieldLabel>Priority</FieldLabel>
          <Controller
            control={form.control}
            name="priority"
            render={({ field }) => (
              <SimpleSelect
                value={field.value}
                onChange={field.onChange}
                options={[...AUTOMATION_PRIORITY_OPTIONS]}
              />
            )}
          />
        </Field>
        <Field>
          <FieldLabel>Subject</FieldLabel>
          <Controller
            control={form.control}
            name="titleTemplate"
            render={({ field }) => (
              <Input
                placeholder="Defaults to the scenario name"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </Field>
        <Field>
          <FieldLabel>Message</FieldLabel>
          <Controller
            control={form.control}
            name="bodyTemplate"
            render={({ field }) => (
              <Textarea
                rows={3}
                placeholder="Defaults to the description"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </Field>
        <ToggleRow
          checked={confirmReceipt}
          onChange={(next) => form.setValue('confirmReceipt', next)}
          label="Request that recipients confirm receipt"
          description="Required before a follow-up ladder can be set: a cascade advances on silence, so without a way to answer every step would fire for everyone."
        />
      </section>

      {confirmReceipt ? (
        <EscalationPlanField
          preset={escalationPreset}
          onPresetChange={(preset) => {
            setEscalationPreset(preset);
            form.setValue('escalationPlan', ESCALATION_PRESETS[preset], {
              shouldDirty: true,
            });
          }}
          plan={ESCALATION_PRESETS[escalationPreset]}
        />
      ) : null}

      <section className="tw:space-y-3">
        <h3 className="tw:m-0 tw:text-sm tw:font-semibold">
          What work it creates
        </h3>
        <FieldDescription>
          Org Action items become real tasks when the scenario launches. Send
          Notification items stay as prompts an operator reviews before sending.
        </FieldDescription>
        <ChecklistConfigEditor
          items={checklistItems}
          onChange={(next) =>
            form.setValue('checklistItems', next, { shouldDirty: true })
          }
          userOptions={userOptions}
          teamOptions={teamOptions}
          addLabel="Add Checklist Item"
          emptyMessage="No checklist items yet."
        />
      </section>

      {errors.length > 0 ? (
        <ul className="tw:m-0 tw:list-none tw:space-y-0.5 tw:pl-0">
          {errors.map((error) => (
            <li key={error} className="tw:text-xs tw:text-destructive">
              {error}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:border-t tw:border-border tw:pt-3">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <div className="tw:flex tw:gap-2">
          {scenario ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="tw:gap-1"
              disabled={archiveScenario.isPending}
              onClick={async () => {
                await archiveScenario.mutateAsync(scenario.id);
                onClose();
              }}
            >
              <Trash2 className="tw:h-4 tw:w-4" />
              Delete
            </Button>
          ) : null}
          <Button
            type="submit"
            size="sm"
            disabled={isSaving || errors.length > 0}
          >
            {scenario ? 'Save' : 'Create scenario'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ScenarioEditor;
